import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
/* ⭐ `FileText` ARRIVED 2026-09-20 and `Paperclip` STAYED — see the header note.
   The two icons now mean two different things on this panel, which is the point. */
import { ChevronDown, ChevronRight, FileText, Inbox } from 'lucide-react';
import { focusScopeLabel, type RailFocus } from '@/lib/railFocus';
import {
    getDocumentConfig, uploadDocument, knownScans,
} from '@/api/client';
import { printDocumentToDialog, printUrlToDialog } from '@/lib/printDocument';
import { AttachedPaperworkPanel, type AttachedTarget } from '@/components/AttachedPaperworkPanel';
import { ScanSourceToggle } from '@/components/ScanSourceToggle';
import { TrayRefreshButton } from '@/components/TrayRefreshButton';
/* ⚠️ `isImageFile` LEFT WITH LocalThumb (2026-09-23). It was the guard that
   decided whether a local scan was worth an <img> at all; with no thumbnail
   there is nothing to decide. TS6133 caught it on the first build.
     ** A HELPER WITH ONE CALLER DIES WITH ITS CALLER. THE COMPILER KNOWS
        THIS BEFORE YOU DO. ** */
import { DocumentThumb } from '@/components/DocumentThumb';
import { TrayCard } from '@/components/PaperworkTrayCard';
import { registerLocalScans, registerLocalFolderWriter } from '@/lib/dnd';
import type { AppointmentDto, DocumentConfigDto, DocumentDto } from '@/api/types';
import { SignDocumentDialog } from '@/components/SignDocumentDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
    folderPickerSupported, pickScanFolder, recallFolder, forgetFolder,
    ensureReadPermission, ensureWritePermission, folderWriteSupported, writeNewFile, deleteFile,
    listScans, prettyBytes,
    type ScanFolderHandle, type ScanFile,
} from '@/lib/scanFolder';
import { SECTION_HEADER } from '@/lib/utils';
import { snapshotFiles } from '@/lib/dnd';
import { useFileUpload } from '@/lib/useFileUpload';
import { useRailSlotClose } from '@/lib/railSlot';

/**
 * THE SCAN FOLDER AND THE UNFILED TRAY (design §1.5).
 *
 * The scheduler points the browser at the site's scan folder once. From then on
 * this lists it — newest first, re-read every few seconds — so a document
 * scanned at the MFP appears here within seconds and can be put on a load
 * without leaving the page.
 *
 * ⭐ ALREADY-FILED SCANS ARE GREYED OUT, and that is the difference between this
 * and dragging from Explorer. On a thirty-truck morning nobody can remember
 * which of Scan_0041 … Scan_0068 they have dealt with, and that is exactly where
 * the Explorer workflow falls apart.
 *
 * ⛔ NOTHING IS FILED AUTOMATICALLY (design §3). A bill of lading on the wrong
 * appointment is worse than one never filed, because it looks right: an unfiled
 * document is visibly missing and somebody chases it, while a confidently
 * misfiled one is found six weeks later by the person disputing the load.
 *
 * ⚠️ IT DEGRADES. Firefox, Safari and every mobile browser cannot do this at
 * all, so they get a plain file input rather than a dead button.
 */

interface Props {
    facilityCode: string;
    /** Today's appointments — what a document can be filed onto. */
    appointments: AppointmentDto[];
    getToken: () => Promise<string>;
    onFiled?: () => void;
    /**
     * ⭐⭐ THE LOAD THE CURSOR IS DWELLING ON, or null for the ordinary tray.
     * ⛔ THE LOCAL-FOLDER HALF OF THE SAME FEATURE. On 2026-09-09 a tray feature
     * built into only one of the two components shipped green and was invisible
     * at Pete's site (§4.14.1). Both get it, always.
     */
    attached?: AttachedTarget | null;
    /**
     * ---- ⭐⭐⭐ THE RAIL'S SCOPE, SO THIS HEADER SPEAKS THE SAME SENTENCE --------
     *
     * > Pete, 2026-09-20: *"Use the same format as other panels Paperwork (all) or
     * > Paperwork (traffix)."*
     *
     * ⛔⛔ BOTH TRAY COMPONENTS TAKE IT, AND THAT IS NOT DUPLICATION FOR ITS OWN
     * SAKE. Exactly one of `ScanFolderPanel` and `PaperworkSliver` renders at a
     * site — SharePoint or local folder — so a header change built into one of them
     * ships green and is invisible at the other half of the estate. That has already
     * happened once (§4.14.1) and the `attached` prop above carries the same warning.
     *
     * ⚠️ NULL IS A VALUE HERE, NOT AN ABSENCE. `focusScopeLabel(null)` is `(ALL)`,
     * which is the correct and deliberate reading of an unscoped board — not a
     * missing label to be hidden.
     */
    focus?: RailFocus | null;
    /**
     * ⭐ WHICH PILE IS ON SCREEN. Seeded from `ScanSourceType` and then owned by
     * the scheduler — see the §1R.16 note below.
     */
    sourceView: 'SharePoint' | 'LocalFolder';
    trayView: 'SharePoint' | 'LocalFolder' | 'Attached';
    onTrayView: (v: 'SharePoint' | 'LocalFolder' | 'Attached') => void;
    /** ⭐ A scan dropped on the Attached icon — files onto the load it shows. */
    onDropAttached?: (e: React.DragEvent) => void;
    /** ⭐ A filed document dropped on the pile icon — moves it back. */
    onDropUnfile?: (e: React.DragEvent) => void;
    /** ⭐ Dropped on "This machine" — MOVES the document out to the scan folder.
        ⛔ Confirmed in an app dialog first: it voids the Dock record, and Chrome's
        own write-permission prompt cannot be restyled or skipped. */
    onDropLocal?: (e: React.DragEvent) => void;
    /**
     * ⭐ Raised by a CLICK on "This machine", never by a config load or a facility
     * switch — see the long note in `Calendar`. `force` means it was clicked while
     * already on this view, which is the gesture the old "Change" link was.
     */
    localPick: { n: number; force: boolean };
    /** ⛔ False when the config could not be read — the toggle must not lie. */
    sharePointConfigured?: boolean;
    /**
     * ---- ⛔⛔ THE LOCAL TRAY HAD NO WAY TO BE TOLD ANYTHING HAD CHANGED -----
     *
     * > Pete, 2026-09-10: *"Ok moved a file from local to sharepoint. Local
     * > doesn't have a refresh so both files are showing up in both spots."*
     *
     * ⛔ `PaperworkSliver` HAS TAKEN THIS PROP SINCE #288 AND THIS PANEL NEVER
     * DID — so every cross-pile move refreshed the cloud tray and left the local
     * one showing yesterday's answer. §4.14.3 again: one of two trays given the
     * plumbing.
     *
     * ⛔⛔ ~~"WHAT CHANGES IS THE GREY-OUT, NOT THE ROW"~~ — SUPERSEDED THE SAME
     * DAY. That was written while filing a local scan COPIED it, so the row
     * legitimately stayed and only went grey. ⭐ Pete: *"sometimes it moves,
     * sometimes it copies. Can we always make it move please?"* — filing now
     * REMOVES the original, so the row goes.
     *
     * ⚠️ THE GREY-OUT STILL EARNS ITS KEEP. `removeEntry` can fail (the file is
     * open in a viewer, the share dropped), and that case is reported rather than
     * hidden — the row comes back on the next read marked `· already filed`,
     * which is exactly the state it describes.
     */
    refreshKey?: number;
}

const POLL_MS = 6_000;

/* ⭐ How long an "N files added" confirmation stays on screen. Same value in both
   trays on purpose — see `lib/useFileUpload.ts`. */
const BANNER_MS = 6_000;

/* ⛔ ~~`Pending`~~ went with the filing form it described. */

export function ScanFolderPanel({
    facilityCode, appointments, getToken, onFiled, attached,
    focus = null,
    sourceView, trayView, onTrayView, onDropAttached, onDropUnfile, onDropLocal, localPick, sharePointConfigured,
    refreshKey = 0,
}: Props) {
    const [open, setOpen] = useState(false);

    /* ⭐ TELL THE RAIL. One line, no prop contracts touched — see
       `lib/railSlot.ts` for why this reports upward instead of being lifted.
       ⚠️ The rail uses it to give an OPEN card the leftover space and to move
       it above the closed ones; a card that never reported would simply keep
       today's behaviour rather than break. */
    /* ⛔⛔ SEE THE MATCHING LINE IN `PaperworkSliver`. This panel returns null on
       a SharePoint view, and a panel that draws nothing must not report a slot —
       that is what left Pete with no scrollbar three times running. */
    /**
     * ⛔⛔ ~~THE ACCORDION~~ REVERTED 2026-09-10, SAME DAY, BY PETE: *"now they
     * don't work. none of them expand. Put it back."*
     *
     * ⚠️ THE FAULT WAS A RENDER/COMMIT RACE, not the idea. Clicking a chevron set
     * `open = true`; on THAT render the rail had not been told yet, so `openCodes`
     * was still empty and the yield signal read TRUE for the panel that had just
     * opened. Both effects then ran in the same commit — the report went up and the
     * panel closed itself. It closed on the way open, every time.
     *
     * ⛔⛔ AND THE SIMULATION THAT PASSED IT TESTED THE WRONG THING. It modelled
     * report → reduce → read as a settle-to-fixpoint loop and converged in two
     * passes. React captures the yield value during RENDER, before the report has
     * updated the context — the simulation had no render/commit ordering in it at
     * all, so it could not have caught this. §4.14, in the check written to prove
     * the accordion safe.
     *
     * ⭐ WHEN IT COMES BACK: report SYNCHRONOUSLY from the click, alongside
     * `setOpen`, so the next render already has the new `openCodes` and the stale
     * window does not exist. An effect is the wrong place for it.
     */
    /* ⭐ DECISION 193 — the two-panel cap. `useRailSlotClose` reports this panel's
       state upward AND closes it when the rail says a third panel pushed it out.
       ⛔ The policy is `nextRailSlots` in lib/railSlot.ts, proven by
       tools/rail-slot-test.cjs — not reasoned about here. */
    useRailSlotClose('PAPERWORK', open, setOpen, sourceView !== 'SharePoint');

    const [handle, setHandle] = useState<ScanFolderHandle | null>(null);
    const [permission, setPermission] = useState<'granted' | 'denied' | 'prompt' | null>(null);
    const [scans, setScans] = useState<ScanFile[]>([]);
    const [known, setKnown] = useState<Set<string>>(new Set());
    const [config, setConfig] = useState<DocumentConfigDto | null>(null);
    /**
     * The document whose load-received stamp is being placed (#110).
     *
     * ⭐ OFFERED AFTER FILING, NOT BEFORE. The stamp reads better with an
     * appointment number on it, and more importantly a scheduler filing a
     * document has just confirmed which load it belongs to — which is the moment
     * they know enough to sign it off. Offered, never applied automatically:
     * "received" is a statement a person makes.
     */
    /**
     * ⭐ TICKED FOR A BATCH DRAG — the same affordance the SharePoint tray has
     * had since #288. ⛔ Keyed on `ref`, the folder's own idempotency key, so a
     * tick survives a re-read and two same-named scans stay distinct.
     */
    const [ticked, setTicked] = useState<Set<string>>(new Set());
    /* ⭐ THE ATTACHED COUNT, reported up by `PaperworkList`. ⚠️ `null` until it has
       fetched, and `null` is not `0` — see the header note below.
       ⛔⛔ AND THE OTHER HALF OF THIS PANEL'S COUNT IS `scans.length`, NOT a `total`
       like the Sliver's. This panel has NO `unfiled` state at all: the note at the
       top of the file explains it stopped fetching unfiled documents, and the
       surviving prose about `unfiled` describes the SLIVER's model, not this one.
         ** TWO PANELS THAT LOOK LIKE COPIES CAN STILL COUNT DIFFERENT THINGS.
            READ THE STATE, NOT THE RESEMBLANCE. ** */
    const [attachedCount, setAttachedCount] = useState<number | null>(null);

    /**
     * ⭐⭐ PREVIEW A FILE THE BROWSER IS ALREADY HOLDING — no upload, no network.
     * ⛔ The object URL is revoked when the viewer closes; leaving them alive
     * pins every previewed scan in memory for the life of the page.
     */
    async function previewLocal(scan: ScanFile) {
        try {
            const file = await scan.handle.getFile();
            /**
             * ⛔ THE BROWSER'S OWN COPY, NOT A ROUND TRIP. `DocumentPreview` fetches
             * by documentId and this file has none — it is not in Dock. An object
             * URL shows the very bytes on the disk, with no upload and no network.
             *
             * ⚠️ REVOKED ON A TIMER, not immediately: revoking before the new tab
             * has read the URL shows a blank page. ⛔ And not never — every
             * un-revoked object URL pins its whole file in memory for the life of
             * the page, which on a scan folder is real memory.
             */
            const url = URL.createObjectURL(file);
            window.open(url, '_blank', 'noopener,noreferrer');
            window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch {
            setError(`Could not open ${scan.name} — it may have been moved.`);
        }
    }

    /**
     * ---- ⭐⭐ PRINT A SCAN THAT IS NOT IN DOCK YET (2026-09-10) -------------
     *
     * > Pete: *"Where are my other icons next to the eye? Like Delete?"*
     *
     * ⛔ THE SHAREPOINT ROW HAS HAD PRINT AND BIN ALL ALONG and this one had a
     * preview and nothing else — §4.14.3 again, the same two trays. ⚠️ Pete asked
     * for parity here explicitly a few hours ago (*"Make it the print, eyeball and
     * delete"*) and only the eye arrived.
     *
     * ⛔ IT CANNOT GO THROUGH `printDocumentToDialog`: that fetches by
     * documentId and this file has none. `printUrlToDialog` is the shared half
     * that prints bytes, and the bytes are already in the browser.
     */
    async function printLocal(scan: ScanFile) {
        setError(null);
        setPrinted(null);
        try {
            const file = await scan.handle.getFile();
            const url = URL.createObjectURL(file);
            const outcome = await printUrlToDialog(url, file.type || '', scan.name);
            setPrinted(outcome.message);
            /* ⚠️ The frame path revokes its own; a tab needs time to read it. */
            window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch {
            setError(`Could not print ${scan.name} — it may have been moved.`);
        }
    }

    /**
     * ---- ⛔⛔ AND THE BIN, WHICH HERE IS FOREVER --------------------------
     *
     * ⚠️ THE SHAREPOINT BIN IS NOT THIS BIN. `deleteScan` puts an item in that
     * site's recycle bin and SharePoint keeps it 93 days. `removeEntry` unlinks
     * the file and NOTHING gets it back — not the Windows shell bin, which the
     * browser is not part of.
     *
     * ⛔ SO IT ALWAYS CONFIRMS, AND THE WORDING SAYS "permanently". A shared
     * tooltip would have been wrong for one of the two trays, which is why
     * `TrayCard` takes `deleteTitle` as a prop.
     */
    const [confirmDelete, setConfirmDelete] = useState<ScanFile[] | null>(null);
    async function doDeleteLocal(targets: ScanFile[]) {
        setConfirmDelete(null);
        const gone: string[] = [];
        const refused: string[] = [];
        for (const scan of targets) {
            try {
                if (!handle) throw new Error('no folder is connected');
                const state = await ensureWritePermission(handle, true);
                if (state !== 'granted') {
                    throw new Error(`${handle.name} is connected for reading only`);
                }
                await deleteFile(handle, scan.name);
                gone.push(scan.name);
            } catch (err) {
                refused.push(`${scan.name}: ${err instanceof Error ? err.message : 'failed'}`);
            }
        }
        setTicked(new Set());
        if (refused.length) setError(refused.join(' · '));
        else if (gone.length) setPrinted(`Deleted ${gone.length === 1 ? gone[0] : `${gone.length} files`}.`);
        void refresh();
    }

    /**
     * ⭐ THE GLIMPSE, FOR FREE. The browser is holding the file, so this costs one
     * `createObjectURL` and no request — see `DocumentThumb`'s note on its two
     * sources. ⛔ Images only; a PDF gets the file icon, deliberately.
     */
    /**
     * ---- ⛔⛔ KEYED ON `scan.ref`, NEVER ON `scan` (2026-09-10) -------------
     *
     * > Pete: *"the thumbnails are gliching because of the refresh, anyway to
     * > stop that?"*
     *
     * ⛔ THE DEPENDENCY WAS THE OBJECT. `refresh()` replaces `scans` with a
     * BRAND-NEW `ScanFile` for every file on every read — the poll fires every
     * six seconds and the new Refresh button fires on demand — so this effect
     * saw a changed dependency each time even when nothing about the file had
     * changed. ⚠️ Its cleanup then REVOKED the live object URL, the `<img>` lost
     * its source, and the row flashed empty until the re-read came back. That is
     * the glitch, and it got worse the moment refreshing got easier.
     *
     * ⭐ `ref` IS name + size + lastModified — the folder's own idempotency key.
     * It is identical across re-reads of an unchanged file and different the
     * moment the file really changes, which is exactly the question this effect
     * should be asking.
     *
     * ⛔ THE HANDLE GOES THROUGH A REF so the effect can use the CURRENT one
     * without depending on its identity. Reading `scan.handle` inside the effect
     * body while it is not a dependency is the stale-closure trap this app has
     * already been bitten by twice (see GanttCalendar's hover handlers).
     */
    /**
     * ---- ⛔⛤ ~~LocalThumb~~ REMOVED 2026-09-23, AT PETE'S REQUEST -----------
     *
     * > *"the thumbnail image of the doc is still flickering, please remove it from
     * > the scanned paperwork panels."*
     *
     * ⚠️ THIS UNDOES SOMETHING HE ASKED FOR. `paperwork-tray-guard` carried
     * *"a hardcoded <FileText/> is what Pete saw on his JPEGs"* - the thumbnail
     * existed because he wanted to tell one scan from another. Its assertions come
     * out with it; the reason is recorded here so the next reader does not
     * "restore" a thumbnail he asked to be rid of.
     *
     * ⛔ THE ROW STILL DRAWS `DocumentThumb`, with NO objectUrl and NO target - so
     * it renders the file-TYPE icon and never loads an image. That keeps the row's
     * shape, keeps the required `icon` prop satisfied, and leaves nothing to flick.
     *
     * ---- ⭐⭐ AND THE CAUSE WAS NOT THE ONE WE FIXED ------------------------
     *
     * 2026-09-10 keyed the effect on `scan.ref` rather than the `ScanFile` object,
     * which was correct - `refresh()` builds a new object per file per read. The
     * flicker survived anyway, because the component was declared INSIDE
     * `ScanFolderPanel`'s body. A component defined in another component's body is
     * a NEW COMPONENT TYPE on every parent render, so React could not reuse the
     * subtree: it unmounted it, destroyed the <img>, re-ran the effect and revoked
     * the object URL, every render.
     *   ** A COMPONENT DEFINED INSIDE ANOTHER COMPONENT IS A NEW TYPE ON EVERY
     *      RENDER. REACT CANNOT REUSE IT; IT CAN ONLY REPLACE IT. **
     *   ** FIXING A DEPENDENCY LIST CANNOT HELP A COMPONENT THAT IS BEING
     *      REMOUNTED. THE EFFECT IS NOT RE-FIRING - IT IS RUNNING FOR THE FIRST
     *      TIME, AGAIN. **
     *
     * ⭐ Hoisting to module scope would very likely have fixed it, and was offered.
     * Pete chose removal. 🔨 If thumbnails are wanted here again: HOIST, do not
     * re-nest.
     */
    const [signing, setSigning] = useState<DocumentDto | null>(null);
    /* ⛔ ~~`busy`~~ REMOVED. It gated the filing form's buttons; with the form
       gone nothing sets it and nothing reads it. ⚠️ Left as a const it would have
       dimmed controls forever on a value that could never change. */
    const [error, setError] = useState<string | null>(null);

    /**
     * ---- ⭐ PRINTING A LABEL FROM THE TRAY (§1AK) --------------------------
     *
     * > Pete, 2026-09-09: *"The tray button on documents in Scanned Paperwork?
     * > Has to be a label printer type icon and I will need a test ZPL doc to
     * > upload there and print"*
     *
     * ⛔ THE OUTCOME IS SHOWN, ALWAYS, SUCCESS OR FAILURE. `printLabel` never
     * throws and always returns a sentence — the whole design note at the top of
     * `useZebraPrint` is that a print which quietly does nothing is the worst
     * outcome on a dock, and it would be thrown away here by an empty catch.
     */
    /* ⛔ ~~`useZebraPrint`~~ went with the label-print icon on the dead document
       rows. A local scan has no documentId, so it never had a label to print —
       the cloud tray keeps the capability. */
    const [printed, setPrinted] = useState<string | null>(null);

    /** The document being filed, and the choices for it. */

    /**
     * ⭐ #150 — this facility reads its scans from SharePoint, so every local-folder
     * control below is not merely unnecessary, it is WRONG to show (migration 162).
     */
    const isSharePoint = config?.scanSourceType === 'SharePoint';

    const supported = folderPickerSupported();

    // ---- config -----------------------------------------------------------
    useEffect(() => {
        if (!facilityCode) return;
        let cancelled = false;
        void (async () => {
            try {
                const res = await getDocumentConfig(await getToken(), facilityCode);
                if (cancelled) return;
                setConfig(res.config);
            } catch { /* the panel still works with the shipped defaults */ }
        })();
        return () => { cancelled = true; };
    }, [facilityCode, getToken]);

    // ---- restore a remembered folder ---------------------------------------
    // ⚠️ NON-INTERACTIVE. The picker cannot be opened without a click, and
    // requesting permission outside a gesture throws — so this only re-checks a
    // grant that already exists. Chrome 122+'s "Allow on every visit" makes that
    // the normal case.
    useEffect(() => {
        // ⛔ Not on a SharePoint facility — re-attaching a remembered local folder
        // would start listing scans from the source the config says is off.
        if (isSharePoint) return;
        let cancelled = false;
        void (async () => {
            const h = await recallFolder();
            if (cancelled || !h) return;
            const state = await ensureReadPermission(h, false);
            if (cancelled) return;
            setHandle(h);
            setPermission(state);
            if (state === 'granted') setOpen(true);
        })();
        return () => { cancelled = true; };
    }, [isSharePoint]);

    const allowed = useMemo(
        () => (config?.allowedExtensions ?? 'pdf,jpg,jpeg,png,tif,tiff').split(',').map(s => s.trim()),
        [config],
    );

    // ---- read the folder ---------------------------------------------------
    const refresh = useCallback(async () => {
        if (!handle || permission !== 'granted') return;
        try {
            const found = await listScans(handle, { allowedExtensions: allowed });
            setScans(found);
            /**
             * ⛔⛔ THE DRAG REGISTRY FOLLOWS THE FOLDER, ON EVERY READ.
             *
             * A `localScan` payload carries only a `ref` — the bytes cannot ride
             * in a `dataTransfer` and reading a file is async while `dragstart` is
             * not. The drop resolves the ref back to a handle through this.
             *
             * ⚠️ REPLACED WHOLESALE, never appended: the folder is the truth, and a
             * map that only grew would hand the drop a handle to a file the scanner
             * has since moved.
             */
            registerLocalScans(found.map(f => ({
                ref: f.ref, name: f.name, getFile: () => f.handle.getFile(),
            })));
            if (found.length > 0) {
                const res = await knownScans(await getToken(), facilityCode, found.map(f => f.ref));
                setKnown(new Set(res.known));
            } else {
                setKnown(new Set());
            }
            setError(null);
        } catch (err) {
            // ⚠️ A share that has gone away must SAY so. Silently showing an
            // empty list reads as "no scans yet" — the one thing it is not.
            setError(err instanceof Error && err.name === 'NotFoundError'
                ? 'That folder is no longer reachable. Check the network drive, then choose it again.'
                : 'Could not read the scan folder.');
        }
    }, [handle, permission, allowed, facilityCode, getToken]);

    /**
     * ---- ⛔⛔ THE LOCAL VIEW IS THE FOLDER ON THIS PC. NOTHING ELSE. --------
     *
     * > Pete, 2026-09-10: *"Uploaded, not on a load yet is not suppose to show
     * > under local correct? These files are in Sharepoint, which isn't showing
     * > any files now????"*
     *
     * ⛔⛔ I SPLIT THIS THE WRONG WAY ROUND AND HIS SENTENCE IS THE FIX. The
     * first cut reasoned that a manual upload "came off this machine", so it
     * rode with the local view. ⚠️ That confuses WHERE A FILE WAS BROWSED FROM
     * with WHERE IT LIVES NOW — and where it lives now is the only thing either
     * view is about.
     *
     * ⭐ THE LINE IS: HAS DOCK GOT IT YET?
     *     LOCAL = files sitting in the scanner's folder on this PC, not yet
     *             uploaded. They have no DocumentId at all.
     *     CLOUD = everything already IN Dock waiting to be filed, however it
     *             arrived — SharePoint ingest or a person clicking Upload.
     *
     * ⛔ SO THIS PANEL NO LONGER FETCHES UNFILED DOCUMENTS. Not filtered to
     * none — not asked for. ⚠️ It also explains why the SharePoint view looked
     * empty: the only unfiled documents at Brighton are Uploads, and the old
     * filter sent both of them to the wrong pile.
     *
     * ⭐ THE STATE STAYS because the render, the counts and the filing flow all
     * read `unfiled`; an empty array is the honest value for "this view holds no
     * Dock documents", and deleting the field would scatter the change.
     */
    /* ⛔ ~~`refreshUnfiled`~~ REMOVED with the list it fed. The local view is the
       folder on this PC; documents already in Dock are the cloud pile's, and the
       split is explained at length on `PaperworkSliver`'s fetch. */


    useEffect(() => { void refresh(); }, [refresh]);

    /**
     * ⭐ AN EXPLICIT ACTION ELSEWHERE RE-READS THIS FOLDER — the same contract
     * `PaperworkSliver` and `CheckInPanel` already have on `refreshKey`.
     *
     * ⛔ SEPARATE FROM THE MOUNT EFFECT ABOVE, and gated on `> 0` so the very
     * first render does not read the folder twice.
     *
     * ⚠️ IT RUNS EVEN WHILE THE SECTION IS FOLDED, unlike the poll. The move
     * that changed the answer happened while the scheduler was looking at the
     * OTHER pile, and a stale grey-out that corrects itself only after somebody
     * expands the panel is exactly the confusion this fixes.
     */
    useEffect(() => { if (refreshKey > 0) void refresh(); }, [refreshKey, refresh]);

    /**
     * ---- ⭐⭐ PUBLISH THE FOLDER AS A WRITE TARGET (2026-09-10) -------------
     *
     * > Pete: *"Still not letting me drag from sharepoint to local."*
     *
     * ⛔ THE HANDLE CANNOT BE LIFTED AND MUST NOT BE COPIED. It is a live
     * browser object with its own permission state; putting it in `Calendar`'s
     * state would give two components two opinions about whether the folder is
     * still reachable. ⭐ So the panel keeps the handle and publishes a
     * FUNCTION — the same shape as `registerLocalScans` directly above.
     *
     * ⛔ UNREGISTERED ON CLEANUP, INCLUDING WHEN THE FOLDER GOES. A writer left
     * behind would report success against a handle whose permission has lapsed,
     * and the file would simply not be there.
     */
    useEffect(() => {
        if (!handle || permission !== 'granted' || !folderWriteSupported(handle)) {
            registerLocalFolderWriter(null);
            return;
        }
        registerLocalFolderWriter({
            folderName: handle.name,
            write: async (fileName, data) => {
                /* ⛔ ASKED FOR AT THE MOMENT OF USE, not when the folder was
                   read. A site that only ever reads scans is never prompted.
                   `true` because a drop is the closest thing to a gesture we
                   get; when Chrome refuses, `writeNewFile` throws and the
                   caller turns that into a sentence. */
                const state = await ensureWritePermission(handle, true);
                if (state !== 'granted') {
                    throw new Error(
                        `The folder ${handle.name} is connected for reading only. `
                        + 'Click the folder name in the Scanned Paperwork panel and choose it '
                        + 'again to allow saving into it.',
                    );
                }
                const written = await writeNewFile(handle, fileName, data);
                /* ⭐ The row appears without waiting for the poll. */
                void refresh();
                return written;
            },
            /* ⛔⛔ THE MOVE'S SECOND HALF. Unrecoverable — see `deleteFile`. It
               shares the write permission, so the same `ensureWritePermission`
               gate applies and the same sentence names the fix. */
            remove: async (fileName) => {
                const state = await ensureWritePermission(handle, true);
                if (state !== 'granted') {
                    throw new Error(
                        `${handle.name} is connected for reading only, so the original could not `
                        + 'be removed.',
                    );
                }
                await deleteFile(handle, fileName);
                void refresh();
            },
        });
        return () => registerLocalFolderWriter(null);
    }, [handle, permission, refresh]);

    // ⚠️ POLLED, because there is no file-watching API. Paused while the filing
    // ⛔ ~~"paused while the filing form is open"~~ — there is no filing form
    // here any more, so there is nothing to pause for. The poll simply reads the
    // folder; a drag in flight is unaffected because the row it carries is
    // resolved by `ref` through the registry, not by list position.
    useEffect(() => {
        if (!open || !handle || permission !== 'granted') return;
        const t = window.setInterval(() => { void refresh(); }, POLL_MS);
        return () => window.clearInterval(t);
    }, [open, handle, permission, refresh]);

    // ---- actions -----------------------------------------------------------
    /**
     * ---- ⛔⛔ ONE PICKER AT A TIME, OR THE PANEL LOCKS UP -------------------
     *
     * > Pete, 2026-09-10: *"After I clicked the Change underscore button, the
     * > buttons in the panel locked up. Can't click them now."*
     *
     * ⛔ `showDirectoryPicker()` CALLED WHILE ONE IS ALREADY OPEN REJECTS, and in
     * Chrome that can leave the picker unusable for the rest of the page's life —
     * every later call fails and every control that opens one reads as dead. ⚠️
     * `pickScanFolder` swallows the rejection (it cannot tell a cancel from a
     * clash), so the symptom is silence, not an error.
     *
     * ⚠️ AND THIS CODE MADE THE CLASH EASY TO CAUSE: switching to "This machine"
     * opens a picker BY ITSELF, so a scheduler who then clicked Change — or the
     * icon again — had two in flight from one gesture.
     *
     * ⛔ A REF, NOT STATE. It must be true on the very next statement, before any
     * re-render, or the second call gets through the gate anyway.
     */
    const picking = useRef(false);

    async function choose() {
        if (picking.current) return;
        picking.current = true;
        try {
            const h = await pickScanFolder();
            if (!h) return;
            setHandle(h);
            setPermission(await ensureReadPermission(h, true));
            setOpen(true);
        } finally {
            /* ⭐ ALWAYS RELEASED. A gate that a thrown error leaves shut is the
               same lockup wearing a different hat. */
            picking.current = false;
        }
    }

    /**
     * ⭐ CLICKING "THIS MACHINE" AGAIN PICKS A FOLDER — the behaviour that lets
     * the "Change" link go. ⛔ Skips the very first render: the nonce starts at 0
     * and an effect that fired on mount would throw a folder picker at anybody
     * opening the board.
     */
    /**
     * ⭐⭐ POINTING AT A LOAD OPENS THE TRAY. This is what "hover does nothing"
     * actually needed: the target arrives, so the section that shows it unfolds.
     * ⛔ Not `attached` — `trayView`. The target is remembered after you leave the
     * attached view (that is what keeps the third icon live), so keying on it would
     * re-open this section every time anything re-rendered.
     */
    useEffect(() => {
        if (trayView === 'Attached') setOpen(true);
    }, [trayView, attached]);

    const lastPickNonce = useRef(localPick.n);
    useEffect(() => {
        if (lastPickNonce.current === localPick.n) return;
        lastPickNonce.current = localPick.n;
        setOpen(true);
        if (!supported) return;
        /* ⭐ RE-CLICKED ON THIS VIEW = "change the folder". ARRIVED HERE WITH NO
           FOLDER = there is nothing to show, so ask. ⛔ Arriving with a folder
           already remembered opens nothing: Pete asked for it to *"open there
           next time"*, not to be asked again. */
        if (localPick.force || !handle) void choose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localPick.n]);

    /**
     * ---- ⭐⭐ SWITCHING TO "THIS MACHINE" OPENS THE PANEL AND THE PICKER -----
     *
     * > Pete, 2026-09-10: *"Issue when go to this machine, now stuck and it
     * > didn't ask for a location????"* … *"don't need all the Choose a scan
     * > folder stuff, just have it open and then select it."*
     *
     * ⛔ TWO SEPARATE FAULTS MADE THAT ONE SYMPTOM. The sliver returned null and
     * THIS panel took over — collapsed, because the two trays keep their own
     * `open` and the scheduler had never opened this one. And the picker was
     * behind a button behind two paragraphs of explanation, inside that
     * collapsed body. The panel looked stuck because everything it had to say
     * was folded away.
     *
     * ---- ⚠️ THE GESTURE IS STILL LIVE WHEN THIS RUNS ------------------------
     *
     * `showDirectoryPicker` needs TRANSIENT USER ACTIVATION, not a synchronous
     * call stack. The activation is granted by the toggle click and lasts
     * seconds; this effect runs in the same task's microtask flush, well inside
     * it. ⛔ If a browser ever refuses anyway the picker simply does not open —
     * so the manual button below STAYS as the fallback, and is the reason this
     * cannot leave the scheduler with no way forward.
     *
     * ⭐ ONCE PER SWITCH. `askedFor` remembers the view we already prompted for,
     * so a re-render — or the user cancelling the picker — does not reopen it in
     * their face.
     */
    /**
     * ⛔⛔ ~~THE SOURCE-CHANGE AUTO-PICKER~~ REMOVED (Pete, 2026-09-10, Denver).
     *
     * It opened a folder picker whenever `sourceView` changed — which includes a
     * FACILITY SWITCH, because that reloads the document config and the config
     * sets the source. ⚠️ So changing to Denver opened a picker nobody asked for,
     * and the next click put a second one in flight and locked the panel.
     *
     * ⭐ The click path above replaces it entirely: a picker is opened by a
     * person, or not at all.
     */

    async function reconnect() {
        if (!handle) return;
        setPermission(await ensureReadPermission(handle, true));
    }

    /**
     * ⛔⛔ ~~startFilingDocument · openDocument · printDocument · isLabelFile~~
     * REMOVED 2026-09-10, with the unfiled-document list they served (§0.3).
     *
     * ⭐ THE COMPILER FOUND THESE, NOT ME. Deleting the dead list turned each of
     * them into "declared but never read" in turn — which is the honest measure
     * of how much of this file existed only to draw a row `TrayCard` already
     * draws. ⚠️ Their equivalents live on in the shared row and in the cloud
     * tray, which is the one place a Dock document is now listed.
     */
    /* ⛔ ~~`startFilingScan`~~ REMOVED 2026-09-10 (§0.3, struck where it lived).
       It backed the "File…" button, which opened a form to choose an appointment. Pete:
       *"Remove the File..."* — dragging the row ONTO the load is choosing it, and
       it is the gesture the SharePoint tray has always had.
       ⚠️ `startFilingDocument` STAYS: the unfiled-document rows still use the
       same form, and only the scan half of it is now unreachable. */


    /** Upload a scan and file it in one step — the scheduler pressed Save once. */

    /**
     * The fallback path: an ordinary file input, for browsers with no picker.
     *
     * ⭐⭐ THE LOOP IS SHARED SINCE 2026-09-08 (`lib/useFileUpload.ts`). This panel
     * carried its own copy for months, and `FileDropZone` — the control created
     * expressly to end those copies — never reached it. ⛔ The reason it could
     * not is that this input is a FALLBACK inside a bigger control, not a box of
     * its own; extracting the loop rather than the component is what let all
     * three screens converge without any of them changing shape.
     *
     * ⚠️ TWO BEHAVIOURS CHANGE HERE, BOTH IMPROVEMENTS, BOTH SAID OUT LOUD
     * rather than smuggled in as "just a refactor":
     *   1. ⭐ A PARTIAL COUNT SURVIVES A FAILURE. "3 of 5 uploaded, then this
     *      went wrong" instead of a bare error after three succeeded — which is
     *      a scheduler re-picking five and getting eight documents.
     *   2. ⭐ The input clears IMMEDIATELY rather than in a `finally`, so the
     *      same file can be picked twice in a row. ⛔ That is only safe because
     *      the loop snapshots first (#288); before the snapshot existed, this
     *      panel's `finally` was the one thing keeping it working while the
     *      sliver's identical code failed silently.
     */
    const picked = useFileUpload({
        /* ⛔ THE EXPIRY LIVES IN THE HOOK so neither tray can ship without it —
           see `useFileUpload`'s note on the fourth two-tray miss. */
        autoClearMs: BANNER_MS,
        getToken,
        upload: (token, f) => uploadDocument(token, facilityCode, f, { source: 'Upload' }),
        /* ⛔ AN UPLOAD FROM HERE LANDS IN THE **CLOUD** PILE, not this one — it is
           in Dock the moment it is uploaded. ⚠️ `onFiled` tells the page, which
           refreshes the tray that actually shows it; refreshing this view would
           re-read the folder and find nothing new, which is what the old
           `refreshUnfiled` did and why it looked like the upload vanished. */
        onDone: () => onFiled?.(),
        failureMessage: 'Could not upload.',
    });


    /**
     * ---- ⛔⛔ SEND ONE STORED DOCUMENT TO THE LABEL PRINTER ----------------
     *
     * ⚠️ THE SERVER READS THE BYTES, NOT THE BROWSER. `getPrintContent` exists
     * because the two obvious reuses are both wrong: the download URL is a
     * storage SAS whose CORS for this origin is unverified, and the preview
     * route refuses a `.zpl` on purpose — its allow-list is about what may be
     * DRAWN, not what may be PRINTED.
     *
     * ⛔ AND THE `^XA`/`^XZ` CHECK HAPPENS TWICE, WHICH IS NOT REDUNDANT. The
     * server checks the FILE; `printLabel` checks what is actually about to go
     * down the wire. A 415 from the first is a bad upload; a refusal from the
     * second would be a bug in us.
     */

    /**
     * ---- ⭐ PRINT A NON-LABEL DOCUMENT (Pete, 2026-09-10) ------------------
     *
     * ⛔ THE LOCAL-FOLDER HALF OF THE SAME BUTTON. It was built into
     * `PaperworkSliver` first and only — the same one-tray-of-two mistake as
     * 2026-09-09 (§4.14.1), caught this time by comparing the two files rather
     * than by Pete finding it on screen.
     *
     * ⚠️ A DIFFERENT PATH FROM THE BARCODE: this opens the OS print dialog for a
     * scan; the barcode sends raw ZPL to a Zebra with no dialog. Sending a PDF
     * down the Zebra path feeds label stock until somebody stops it.
     */
    async function printPaper(d: DocumentDto) {
        setError(null);
        setPrinted(null);
        const outcome = await printDocumentToDialog(
            getToken, { kind: 'document', documentId: d.documentId }, d.fileName);
        setPrinted(outcome.message);
    }

    /**
     * ⚠️ WHICH ROWS GET THE BUTTON, AND WHY IT IS NOT ALL OF THEM.
     *
     * ⛔ A ZEBRA HANDED A PDF DOES NOT REFUSE IT — it consumes it as commands
     * and feeds label stock until somebody physically stops it. The server
     * refuses that with a 413/415, but a button offered on every row is an
     * invitation to find out, and the person finding out is standing at the
     * printer.
     *
     * ⭐ SO THE EXTENSION IS THE GATE, matching `extensionAllowed` server-side.
     * ⚠️ It is a HINT, not the control — `printContent` still checks the bytes.
     */

    /* ⛔ ~~`attachable`~~ was the filing form's list of loads to choose from.
       The choice is the drop target now. */

    /* ⛔ ~~`waiting`~~ was the header badge's count, and the header carries no
       badge any more (Pete, 2026-09-10: *"don't need the 2 waiting, remove it"*).
       ⚠️ Deleted rather than left unused: a count nothing renders is a thing the
       next reader has to work out is dead. */

    const signedAppointment = signing?.appointmentId
        ? appointments.find(a => String(a.appointmentId) === String(signing.appointmentId))
        : undefined;

    /**
     * ⛔ NOTHING AT ALL ON A SHAREPOINT FACILITY.
     *
     * ⚠️ THIS FIRST SHIPPED AS A NOTE POINTING AT THE OTHER PANEL, and Pete's reply was
     * *"I am still confused on the right-column"* — fairly, because a section that
     * exists only to say it does nothing is still a section to read. The paperwork tray
     * now sits immediately above this in the same column, so there is nothing left to
     * point at.
     *
     * ⛔ AND IT IS THE RULE, NOT JUST TIDINESS: migration 162 says only one source is
     * ever consulted. This panel reads a LOCAL folder; on a SharePoint site it must not
     * be on screen at all.
     */
    /**
     * ---- ⛔⛔ §1R.16 IS REVERSED, DELIBERATELY (Pete, 2026-09-10) -----------
     *
     * > *"I think, on Scanned Paperwork panel, we have two icons, one showing
     * > the Network driver or Sharepoint icon and one showing a local drive so
     * > they can swap the view."* … asked whether this reverses the rule:
     * > **"Yes - reverses."**
     *
     * ⛔ MIGRATION 162 SAID "ONLY ONE SOURCE IS EVER CONSULTED", and the reason
     * it gave was: *"a scan arriving from a source the screen says is off is
     * untraceable."*
     *
     * ⭐ THE DANGER IT NAMED WAS A **SILENT FALLBACK**, NOT A DELIBERATE CHOICE.
     * A scheduler switching the view, with the panel saying which source it is
     * showing, is MORE traceable rather than less — and every document still
     * records its own `Source`, so provenance is unchanged.
     *
     * ⚠️ THE OPERATIONAL REASON IS REAL: a scanner cannot write to SharePoint
     * without an Entra sign-in per scan, so sites go scanner → email → Power
     * Automate → SharePoint and wait. Scanning to a local folder is immediate
     * (§1AO). Forcing one source made the fast path unavailable at a site
     * configured for the slow one.
     *
     * ⛔ SO `ScanSourceType` NOW MEANS "WHICH VIEW OPENS FIRST", NOT "WHICH
     * SOURCE EXISTS". `sourceView` is the live choice and it comes from above,
     * because both trays need it and neither owns it.
     */
    if (sourceView === 'SharePoint') return null;

    return (
        /**
         * ---- ⛔⛔ `flex min-h-0 flex-col` — WITHOUT IT THE SCROLLERS BELOW ARE
         *          DECORATION (2026-09-10) ------------------------------------
         *
         * > Pete, for the third time: *"also still no scrollbar"*.
         *
         * ⛔ I PUT `min-h-0 overflow-y-auto` ON BOTH BODIES AND LEFT THIS ROOT AS
         * `<div className="border-t">` — A PLAIN BLOCK. ⚠️ `min-height: 0` is a
         * FLEX-ITEM rule and `overflow-y: auto` needs a height smaller than the
         * content to have anything to scroll. A block parent hands its child the
         * child's own content height, so the body grew, the rail's
         * `overflow-hidden` clipped it, and NO SCROLLBAR APPEARED ANYWHERE —
         * which is precisely what Pete photographed.
         *
         * ⛔⛔ AND THE GUARD SAID GREEN, because it counted the two classes on the
         * bodies and never asked what they were a child OF. §4.14 in one line: a
         * check more confident than its scan. `paperwork-tray-guard` now asserts
         * this root too, in both trays.
         *
         * ⭐ THE CHAIN THAT MAKES A SCROLLBAR: the rail slot is
         * `flex min-h-0 flex-1 flex-col overflow-hidden` → this root is a
         * shrinkable flex column → the header is `shrink-0` → the body is
         * `min-h-0 overflow-y-auto` and gets the leftover. Break any link and the
         * bottom of the list is simply invisible. `PaperworkSliver` has had this
         * exact root all along, which is why only the LOCAL view was ever wrong.
         */
        <div className="flex min-h-0 flex-col border-t">
            {/**
              * ---- ⭐⭐ THE SAME HEADER AS THE SLIVER, TO THE PIXEL (2026-09-10) --
              *
              * > Pete: *"Also lost its chevron."*
              *
              * ⛔ IT NEVER HAD ONE. It had `▾`/`▸` TEXT while the sliver had a real
              * `ChevronDown`/`ChevronRight` — and because exactly one of these two
              * panels ever renders, switching source swapped the header for a
              * different-looking one and read as the chevron disappearing.
              *
              * ⚠️ THAT IS THE COST OF TWO IMPLEMENTATIONS OF ONE HEADER. They are
              * now identical: chevron icon, file icon, title, one count, toggle. A
              * scheduler switching source should see the SAME bar with a different
              * pile under it.
              *
              * ⛔ AND THE COUNT IS ONE BADGE, NOT TWO. Pete: *"Not sure I need to
              * see the 2 Unfiled either."* "to file" and "unfiled" were two names
              * for two halves of the same tray; the header says how much is waiting
              * and the list below says what it is.
              *
              * ⛔ NOT STICKY ANY MORE, for the reason written on the sliver's header:
              * sticky is a promise about ONE scroll container and this rail nests
              * three. The row is `shrink-0` outside the scroller instead.
              */}
            <div
                className={`shrink-0 bg-card flex w-full items-center gap-2 px-3 py-2 ${SECTION_HEADER}`
                                        /* ⛔ ~~`bg-cyan-50` WHILE ATTACHED~~ REVERTED with the title colour
                                           (Pete, 2026-09-10). ⚠️ `bg-card` STAYS and is not decoration:
                                           SECTION_HEADER is semi-transparent, and the drop tray's highlight
                                           shows straight through an untinted header. */}
            >
                <button
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left hover:opacity-80"
                >
                    {/* ⛔⛔ `shrink-0`, OR THE FLEX ROW CRUSHES THEM TO ZERO WIDTH and
                        the chevron is "in the code" and not on the screen — see the
                        matching note in `PaperworkSliver`, which is where Pete saw it. */}
                    {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                    {/**
                      * ---- ⭐⭐ THE TWO MODES DO NOT LOOK ALIKE (2026-09-10) ---------
                      *
                      * > Pete: *"lets leave the color of Scanned Paperwork and its icon
                      * > the same it is now but lets change the color of Attached
                      * > Paperwork to a new color (permanent) and lets put a Paperclip on
                      * > the Attached Paperwork ICon so its different than Scanned
                      * > Paperwork. Maybe we use a different icon for the scanned
                      * > paperwork - seems to similiar to others."*
                      *
                      * ⛔ ONE PANEL, TWO MODES, AND UNTIL NOW ONE APPEARANCE. Both drew
                      * `FileText` in the default foreground and changed only the words —
                      * so a scheduler who glanced at the rail could not tell whether they
                      * were looking at the QUEUE or at ONE LOAD'S dossier. ⚠️ Those two
                      * answer different questions and dragging into the wrong one files
                      * paperwork on the wrong thing.
                      *
                      * ⭐ THE PAPERCLIP IS NOT AN ARBITRARY PICK. It is already the
                      * paperwork mark on every arrival card's tool strip, so "paperclip =
                      * what is attached to this load" is a word the app already speaks.
                      *
                      * ⛔ AND `FileText` WENT because Pete is right that it read as
                      * generic — `FilePen` is WMS Orders two panels down and the two are
                      * a glance apart. `Inbox` says what this pile actually is: a work
                      * queue things arrive in and leave from.
                      *
                      * ⚠️ CYAN, NOT VIOLET OR EMERALD. Violet is the dedication colour on
                      * the grid (`bg-violet-600`) and emerald means SIGNED on every
                      * document row; reusing either would make a mode look like a status.
                      * Cyan is unused anywhere in this app.
                      */}
                        {/**
                          * ---- ⛔⚤ THE TITLE ICON IS A DOCUMENT NOW (Pete, 2026-09-20) --
                          *
                          * > *"The Paperwork title gets the document icon to its left (just
                          * > like the other panels)"* — and, in the same breath, *"the
                          * > sentence under the attachment gets the paperclip/attach icon,
                          * > not the document icon."*
                          *
                          * ⛔⛤ ~~<Paperclip className="… text-cyan-700" />~~ on the title.
                          * ⭐⭐ THE PAPERCLIP DID NOT LEAVE, IT MOVED DOWN ONE LEVEL —
                          * `PaperworkList` renders it in place of the suppressed "signing
                          * and removed-document history" sentence. Which is a promotion for
                          * the symbol, not a demotion: it now marks the one thing that is
                          * ATTACHED-but-elsewhere rather than sitting on a panel name.
                          *
                          * ⛔⛔ AND IT OVERRULES A 2026-09-10 ARGUMENT THAT IS STILL QUOTED
                          * TWENTY LINES UP. That day `FileText` was dropped for reading
                          * "generic", with `FilePen` on WMS Orders a glance away. ⚠️ Pete
                          * has now asked for exactly the icon that argument rejected, and
                          * his reason beats it: the other four rails all wear a plain mark
                          * of WHAT THEY ARE, and this panel was the only one wearing a mark
                          * of what it DOES.
                          *   ** AN ICON CHOSEN TO BE DISTINCTIVE AND AN ICON CHOSEN TO
                          *      MATCH ITS NEIGHBOURS ARE TWO DIFFERENT JOBS. THE ROW OF
                          *      PANELS DECIDES WHICH ONE IS BEING DONE. **
                          * ⚠️ `FilePen` (WMS Orders) AND `FileText` ARE STILL A GLANCE
                          * APART. That cost is real and is now paid deliberately — the
                          * cyan colour is what separates them, and it stays.
                          *
                          * ⛔ SCANNED MODE KEEPS `Inbox`, UNTOUCHED. It is a work queue,
                          * not a dossier, and nothing Pete asked for touches it.
                          */}
                    {trayView === 'Attached'
                        ? <FileText className="h-3.5 w-3.5 shrink-0 text-cyan-700" />
                        : <Inbox className="h-3.5 w-3.5 shrink-0" />}
                    {/* ⛔ THE SOURCE WORD IS STATED HERE because the toggle is icons
                        only — §1R.16's requirement, moved, not dropped. */}
                    {/* ⛔⛔ NO SOURCE WORDING — Pete, 2026-09-10. See the long note on
                        the matching line in `PaperworkSliver` for what that costs
                        §1R.16 and why it is recorded rather than quietly dropped. */}
                    {/* ⭐ THE NAME WEARS THE ICON'S COLOUR (Pete, 2026-09-10). This
                        reinstates what was struck earlier the same day — the full
                        reasoning, and why that is not a contradiction, is in
                        `PaperworkSliver`'s copy of this header. ⛔ BOTH TRAYS DRAW THIS
                        HEADER and a rule applied to one of two is how a feature shipped
                        green and invisible at Pete's site on 2026-09-09 (§4.14.1). */}
                        {/**
                          * ---- ⛔⚤ `Paperwork (ALL)` / `Paperwork (TRAFFIX)` -----------
                          *
                          * > Pete, 2026-09-20: *"Too much going on for Attached Paperwork.
                          * > Change it to just 'Paperwork'. Use the same format as other
                          * > panels Paperwork (all) or Paperwork (traffix)."*
                          *
                          * ⛔⛤ ~~'Attached Paperwork'~~ — the word ATTACHED went because it
                          * described the MODE, and the mode is already stated by the icon
                          * beside it and the toggle to its right. Three statements of one
                          * fact, and the one thing the header never said was WHOSE
                          * paperwork.
                          *
                          * ⭐⭐ `focusScopeLabel` IS THE SAME FUNCTION THE FOUR RAILS CALL.
                          * Not a lookalike string built here — that is how the rails' label
                          * drifted into two shapes on 2026-09-19, and the fix was to have
                          * one function own the wording.
                          *   ** IDENTICAL WORDING MAINTAINED BY COPYING IS WORDING THAT
                          *      DIVERGES. **
                          *
                          * ⚠️ SCANNED MODE IS NOT SCOPED AND DOES NOT PRETEND TO BE. The
                          * scan folder is a facility-wide pile with no client, carrier or
                          * load attached to it yet — that is the definition of the queue.
                          * A `(TRAFFIX)` on it would claim a filter that does not exist.
                          */}
                    {/**
                      * ---- ⛔⛤ THE TITLE STOPS EATING ITS OWN SCOPE (2026-09-22) ----
                      *
                      * > Pete, with a screenshot of `Paperwork (Appt - A0…`:
                      * > *"needs to be formatted the same way as (app - ) on History"*
                      *
                      * ⛔⛤ ~~`Paperwork ${focusScopeLabel(focus)}` inside one truncating
                      * span~~ — the scope was part of the TITLE STRING, so `truncate`
                      * cut the appointment number in half.
                      *   ** A LABEL CONCATENATED INTO A TRUNCATING STRING IS A LABEL
                      *      THAT DISAPPEARS EXACTLY WHEN IT IS LONGEST. **
                      *
                      * ⛔⛔ THE SAME EDIT IS IN `PaperworkSliver.tsx`, AND IT HAD TO BE.
                      * These two headers were byte-identical (§1R.16); fixing one would
                      * have left a SharePoint site and a local-folder site describing
                      * different screens, with nothing on either to say so.
                      *   ** A HEADER RENDERED IN TWO FILES IS TWO HEADERS THAT WILL
                      *      DISAGREE. **
                      * ⬜ Extraction into one component is the real fix — logged, not
                      * done mid-flight.
                      */}
                    <span className={`truncate text-sm font-medium${
                        trayView === 'Attached' ? ' text-cyan-700' : ''}`}>
                        {trayView === 'Attached' ? 'Paperwork' : 'Scanned Paperwork'}
                    </span>
                    {trayView === 'Attached' && (
                        <span
                            className="shrink-0 self-center text-[10px] leading-none text-muted-foreground"
                            title={`Showing ${focusScopeLabel(focus)}`}
                        >
                            {focusScopeLabel(focus)}
                        </span>
                    )}
                    {/* ⛔ ~~the waiting badge~~ removed 2026-09-10 at Pete's request; it
                        rides on the way back out of attached mode instead. */}
                </button>
                {/* ⛔ SIBLING OF THE BUTTON, NOT A CHILD — a button inside a button
                    is hoisted out by the browser and its click is swallowed. */}
                {/* ⛔ THREE ICONS ON EVERY VIEW, and the "← Scanned N" chip is gone —
                    see the note in `PaperworkSliver`. */}
                <ScanSourceToggle
                    value={trayView}
                    onChange={onTrayView}
                    sharePointConfigured={sharePointConfigured}
                    localSupported={supported}
                    attachedAvailable={attached !== null}
                onDropAttached={onDropAttached}
                onDropUnfile={onDropUnfile}
                onDropLocal={onDropLocal}
                />
                {/* ⭐ UPPER RIGHT, AFTER THE TOGGLE — Pete, 2026-09-10. See the
                    component for why it is here and not in the body. ⛔ It re-reads
                    the FOLDER; on a folded panel the poll is off, so this is the
                    only thing that will. */}
                {/**
                  * ---- ⭐⭐ THE HEADER COUNT (Pete, 2026-09-22) ------------------
                  *
                  * > *"There also should be a number (like all the other panels) next
                  * > to the refresh button saying how many documents in which ever of
                  * > the 3 paperwork icons is selected."*
                  *
                  * ⭐ IT FOLLOWS THE SELECTED ICON: attached mode counts THIS LOAD's
                  * documents, the local view counts the scans in the folder.
                  *
                  * ⛔⛔ `null` RENDERS NOTHING. The attached count is unknown until
                  * `PaperworkList` has fetched, and a `0` on a load nobody has read
                  * asserts the load has no paperwork.
                  *   ** A COUNT OVER AN UNASKED QUESTION IS NOT ZERO, IT IS NOTHING. **
                  *
                  * ⚠️ `scans.length` IS NOT GUARDED, because it always has a value —
                  * an empty folder honestly IS zero, and hiding that would lose the
                  * one signal that says the folder was read successfully.
                  */}
                {(trayView === 'Attached' ? attachedCount : scans.length) !== null && (
                    <span
                        className="shrink-0 self-center text-xs text-muted-foreground"
                        title={trayView === 'Attached'
                            ? 'Documents filed on this appointment'
                            : 'Scans in the connected folder'}
                    >
                        {trayView === 'Attached' ? attachedCount : scans.length}
                    </span>
                )}
                <TrayRefreshButton
                    onClick={() => void refresh()}
                    title="Re-read the scan folder"
                />
            </div>

            {/**
              * ⛔⛔ NOT GATED ON `open` — see the matching note in `PaperworkSliver`.
              * Hovering a load is an explicit request to see its paperwork, and this
              * panel starts collapsed, so `{open && attached}` drew nothing however
              * correctly the prop arrived.
              */}
                        {/**
              * ---- ⛔⛔ ATTACHED MODE IS GATED ON `open` AGAIN, AND OPENS ITSELF --
              *
              * > Pete, 2026-09-10: *"Attached paperwork is collapsing too far up.
              * > should only goto the bottom of Driver Checkin... ish"* — with a
              * screenshot showing the chevron CLOSED and the list rendering anyway.
              *
              * ⛔ I UNGATED THIS FROM `open` TO FIX "hover does nothing", AND THAT WAS
              * THE SHORTCUT. The real fault was that hovering set state and nothing
              * OPENED the section; drawing the body outside the fold papered over it
              * and cost two things at once — the chevron stopped meaning anything, and
              * the body escaped the rail's sizing, so it grew until the driver list
              * was squeezed to nothing.
              *
              * ⭐ SO THE HOVER OPENS THE SECTION (see the effect on `attached`), and
              * the body renders inside the normal open-gated flow like every other
              * panel. ⚠️ The rail's `min-h-[6rem]` on the driver list and the bounded
              * panel height then apply — which is exactly Pete's "to the bottom of
              * Driver Check-Ins, ish".
              */}
            {open && trayView === 'Attached' && attached && (
                /**
                  * ⛔⛔ `min-h-0 overflow-y-auto` — THE SCROLLER THIS PANEL NEVER HAD.
                  *
                  * > Pete, 2026-09-10: *"Still no scrollbar. Can't see the two docs
                  * > there."*
                  *
                  * ⛔ `PaperworkSliver` HAS HAD BOTH CLASSES ALL ALONG; THIS FILE HAD
                  * `space-y-2 p-3` AND NOTHING ELSE. Two trays, one of them given the
                  * fix — §4.14.3 for the fifth time this week, and the reason Pete only
                  * ever saw it in the LOCAL view: on a SharePoint site the other
                  * component renders and scrolls perfectly.
                  *
                  * ⚠️ `min-h-0` IS HALF THE FIX, NOT TIDYING. A flex child defaults to
                  * `min-height: auto`, which means it REFUSES TO SHRINK BELOW ITS
                  * CONTENT — so `overflow-y-auto` alone would never have a height to
                  * overflow, the box would grow instead, and the rail would clip it
                  * with no scrollbar anywhere. That is exactly the shape of the note
                  * already written on the driver list in `CheckInPanel`.
                  */
                <div className="min-h-0 space-y-2 overflow-y-auto p-3 text-xs">
                    {/* ⛔ Same second mode as PaperworkSliver, from the same shared
                        component — see AttachedPaperworkPanel's note on why this is
                        not two implementations. */}
                    <AttachedPaperworkPanel
                        target={attached}
                        facilityCode={facilityCode}
                        getToken={getToken}
                        onChanged={() => { onFiled?.(); }}
                        onSign={(d) => setSigning(d)}
                        onPrint={(d) => void printPaper(d)}
                        onCount={setAttachedCount}
                    />
                </div>
            )}

            {/* ⛔ `trayView`, NOT `!attached`. The target is REMEMBERED after you leave
                the attached view — that is what keeps the third icon live — so
                gating on it would blank the scans the moment anything had ever
                been hovered. */}
            {open && trayView !== 'Attached' && (
                /* ⛔ THE SAME TWO CLASSES AS THE ATTACHED BODY ABOVE. Both bodies of
                   both trays scroll, or the next feature lands in three of four
                   again — `paperwork-tray-guard` counts them now. */
                <div className="min-h-0 space-y-2 overflow-y-auto p-3 text-xs">
                    {/* ⛔ ~~THE TOGGLE WAS HERE~~ and moved ONTO THE HEADER ROW
                        (2026-09-10). It was in the body because the header used to be
                        a <button>; it is a flex row now, so the constraint is gone and
                        the toggle is visible while the section is folded. */}
                    {/* ---- connecting the folder --------------------------- */}
                    {!supported ? (
                        // ⚠️ NOT A DEAD BUTTON. Firefox and Safari cannot do this,
                        // and a scheduler on one must still be able to file.
                        <div className="space-y-1.5">
                            <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-slate-700">
                                This browser cannot open a folder directly — that needs Edge or
                                Chrome. You can still add paperwork by choosing files.
                            </div>
                            <input
                                type="file"
                                multiple
                                /* ⛔ SNAPSHOT, CLEAR, RUN -- in that order. See the
                                   note on `picked` above and #288. */
                                onChange={(e) => {
                                    const files = snapshotFiles(e.target.files);
                                    e.target.value = '';
                                    void picked.run(files);
                                }}
                                className="text-[11px]"
                            />
                        </div>
                    ) : !handle || permission !== 'granted' ? (
                        /**
                          * ---- ⭐ THE FALLBACK, NOT THE FRONT DOOR (2026-09-10) ----
                          *
                          * > Pete: *"don't need all the Choose a scan folder stuff,
                          * > just have it open and then select it."*
                          *
                          * ⛔ THE PICKER NOW OPENS ITSELF on the switch to "This
                          * machine" — see the effect beside `choose()`. ⚠️ This row
                          * is what is left when that did not happen: the scheduler
                          * cancelled it, or a browser refused the activation. It is
                          * ONE line with ONE button, because reaching it means
                          * something already did not go to plan and two paragraphs
                          * of reassurance is not the help needed at that moment.
                          *
                          * ⛔ IT CANNOT BE DELETED. Without it a cancelled picker
                          * leaves an empty panel and no way to try again — which is
                          * the exact fault being fixed.
                          */
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-muted-foreground">
                                {config?.scanFolderHint
                                    ? <>No folder yet — pick <span className="font-mono">{config.scanFolderHint}</span>.</>
                                    : 'No scan folder chosen yet.'}
                            </span>
                            <div className="flex shrink-0 gap-2">
                                <button
                                    type="button"
                                    onClick={() => void (handle ? reconnect() : choose())}
                                    className="rounded border px-2 py-1 text-[11px] font-medium"
                                >
                                    {handle ? 'Reconnect' : 'Choose folder…'}
                                </button>
                                {handle && (
                                    <button
                                        type="button"
                                        onClick={() => { void forgetFolder(); setHandle(null); setPermission(null); }}
                                        className="rounded border px-2 py-1 text-[11px] text-muted-foreground"
                                    >
                                        Forget it
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        /**
                          * ⛔ ~~"Watching (folder)   Change"~~ REMOVED (Pete, 2026-09-10):
                          * *"this Change is the same as the click of the Local button
                          * correct?, if so, remove it, not necessary."*
                          *
                          * ⚠️ IT WAS NOT THE SAME WHEN HE ASKED — the icon only picked a
                          * folder when none was remembered — so the link was deleted AND
                          * the icon was given the second job, in `Calendar`'s
                          * `localPick`. Deleting it alone would have left no way to
                          * change folders at all.
                          *
                          * ⛔ AND IT WAS THE CONTROL THAT LOCKED THE PANEL UP: clicking it
                          * after the auto-picker had already opened one put two
                          * `showDirectoryPicker` calls in flight. See `choose()`.
                          */
                        /**
                          * ⭐ ONE LINE SAYING WHICH FOLDER IS BEING WATCHED.
                          *
                          * > Pete, 2026-09-10: *"The local view doesn't show
                          * > folders?"* — looking at a completely blank panel.
                          *
                          * ⛔ IT WAS BLANK BECAUSE I REMOVED TWO THINGS AT ONCE: the
                          * "Watching X · Change" row (he asked for Change gone) and
                          * "Nothing in the scan folder yet" (he asked for that gone
                          * too). Each removal was right on its own; together they left
                          * a connected, empty folder rendering NOTHING AT ALL, which
                          * cannot be told from a broken panel.
                          *
                          * ⚠️ THE FOLDER NAME IS NOT THE "wording" HE ASKED ME TO
                          * DROP. That was the SOURCE label — "This machine" — which
                          * the lit icon already says. This is which folder on that
                          * machine, and nothing else on screen carries it.
                          *
                          * ⭐ NO BUTTON. Clicking the "This machine" icon re-opens the
                          * picker; that is what replaced "Change".
                          */
                        <div className="text-[11px] text-muted-foreground">
                            <span className="font-mono">{handle?.name}</span>
                            {scans.length === 0 ? ' — empty' : ''}
                        </div>
                    )}

                    {/**
                      * ⚠️ TWO ERROR SOURCES, DELIBERATELY BOTH RENDERED. `error` is
                      * this panel's own (filing, folder, permission); `picked.error`
                      * is the shared upload loop's. ⛔ Collapsing them into one
                      * state would mean an upload failure silently replacing a
                      * filing failure the scheduler had not read yet.
                      */}
                    {picked.added !== null && (
                        <div className={picked.error
                            ? 'text-[11px] text-muted-foreground'
                            : 'text-[11px] text-emerald-700'}>
                            {picked.added} file{picked.added === 1 ? '' : 's'}{' '}
                            {picked.error ? 'did upload before that.' : 'added.'}
                        </div>
                    )}
                    {picked.error && (
                        <div className="rounded border border-destructive/40 bg-destructive/10 px-2 py-1 text-[11px] text-destructive">
                            {picked.error}
                        </div>
                    )}
                    {error && (
                        <div className="rounded border border-destructive/40 bg-destructive/5 px-2 py-1 text-destructive">
                            {error}
                        </div>
                    )}
                    {/* ---- ⛔⛔ THE PRINT OUTCOME, AND IT IS NOT RED ----------
                        ⚠️ MOST OF WHAT LANDS HERE IS NOT A FAULT. "No label printer
                        is configured for this document type at this site" is the
                        CONFIGURED answer (runbook B3), and "this machine has no
                        printer called X" names a fix. ⭐ Painting all of it as an
                        error trains people to ignore the line that also carries
                        "Sent to Dock 4 Zebra" — which is the one confirmation that
                        a silent print actually happened. */}
                    {printed && (
                        <div className="flex items-start justify-between gap-2 rounded border bg-muted/40 px-2 py-1 text-[11px]">
                            <span>{printed}</span>
                            <button
                                type="button"
                                onClick={() => setPrinted(null)}
                                className="shrink-0 underline"
                            >
                                dismiss
                            </button>
                        </div>
                    )}

                    {/* ---- the filing form --------------------------------- */}
                    {/**
                      * ⛔⛔ ~~THE FILING FORM~~ REMOVED 2026-09-10 (§0.3).
                      *
                      * It asked "which load is this for?" and was opened by
                      * `startFilingScan` (gone — dragging onto the load IS choosing
                      * it) and `startFilingDocument` (gone with the document list
                      * this tray no longer has). ⚠️ `setPending` was left being
                      * called with `null` twice and nothing else — a form nothing
                      * could open, which `tsc` cannot see because the state is
                      * "used" by the render that draws it.
                      *
                      * ⭐ THE DEPLOY GUARD FOUND IT, not the compiler: a tray that
                      * lists no documents had no business referencing a documentId.
                      */}

                    {/* ---- the folder -------------------------------------- */}
                    {permission === 'granted' && scans.length > 0 && (
                        /**
                         * ⛔ IT SCROLLS, AND BEFORE THIS IT DID NOT (2026-08-27).
                         *
                         * Pete: *"there is no scroll bar on the scanned paperwork."*
                         *
                         * ⚠️ THIS RENDERS EVERY FILE IN THE WATCHED FOLDER. A dock that
                         * scans all day has hundreds, and an unbounded list pushed the
                         * unfiled tray, Labor and Communications off the bottom of the
                         * rail — so the panels BELOW it became unreachable, which is a
                         * worse fault than the list itself being long.
                         *
                         * ⭐ max-h + overflow-y-auto, NOT a "show first 20". A cap that
                         * hides files is how a scheduler concludes a scan never arrived;
                         * the whole point of this list is that everything in the folder
                         * is accounted for.
                         *
                         * ⚠️ pr-1 SO THE BAR DOES NOT SIT ON THE ROWS. Without it the
                         * scrollbar overlays the right-hand action on every line.
                         *
                         * ⛔⛔ THE FRAGMENT IS NOT TIDYING. This branch now renders TWO
                         * siblings — the selection bar and the list — and `&& (` takes
                         * exactly one element. ⚠️ A braced JSX comment placed directly
                         * after `&& (` is ALSO a child rather than an expression, which
                         * is the same parse error CheckInPanel's tool-strip note warns
                         * about and which this edit made on its first attempt.
                         */
                        <>
                        {/**
                          * ---- ⭐⭐ TICK SEVERAL, ACT ON ALL OF THEM ---------------
                          *
                          * > Pete: *"When I checkbox more than one item, can I move
                          * > them together, or delete them together?"*
                          *
                          * ⭐ MOVING TOGETHER ALREADY WORKED and was invisible: a drag
                          * that starts on a TICKED row carries the whole selection
                          * (see `dragItems` below), the desktop-file-manager rule.
                          * ⚠️ Nothing on screen said so, so the tick boxes read as
                          * decoration — which is exactly why he asked.
                          *
                          * ⛔ DELETING TOGETHER DID NOT. This bar is the only control
                          * that acts on the selection rather than on one row, and it
                          * exists only while there IS a selection.
                          */}
                        {ticked.size > 0 && (
                            <div className="mb-1 flex items-center justify-between gap-2 rounded border
                                            bg-muted/40 px-2 py-1 text-[11px]">
                                <span>
                                    {ticked.size} selected · drag any one of them to move them together
                                </span>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setTicked(new Set())}
                                        className="underline"
                                    >
                                        clear
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setConfirmDelete(scans.filter(x => ticked.has(x.ref)))}
                                        className="rounded px-1.5 py-0.5 text-destructive hover:bg-destructive/10"
                                    >
                                        Delete {ticked.size}
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="max-h-72 space-y-1 overflow-y-auto pr-1">
                            {/**
                              * ---- ⭐⭐ THE SAME ROW THE SHAREPOINT TRAY DRAWS ---------
                              *
                              * > Pete, 2026-09-10: *"Should all have the same look and
                              * > feel if its a local repository or a sharepoint one?"* …
                              * > *"the File... button on local doesn't match the Print,
                              * > eyeball and delete on the sharepoint share."*
                              *
                              * ⛔ ~~A HAND-WRITTEN ROW WITH A "File…" BUTTON~~ REPLACED BY
                              * `TrayCard`. It was a different row because it was written
                              * in a different file, and that is the only reason it lacked
                              * a thumbnail, a tick box, the icon strip and — the thing
                              * Pete could not do at all — DRAG.
                              *
                              * ⭐ "File…" IS GONE BECAUSE DRAG REPLACES IT. The button
                              * opened a form to choose a load; dragging onto the load IS
                              * choosing it, and it is the gesture the other tray has
                              * always had.
                              *
                              * ⚠️ THE THUMBNAIL COSTS NO NETWORK HERE. The browser is
                              * holding the file already, so `objectUrl` draws it — see
                              * `DocumentThumb`'s note on why there are two sources.
                              */}
                            {scans.map(scan => {
                                const filed = known.has(scan.ref);
                                return (
                                    <TrayCard
                                        key={scan.ref}
                                        name={scan.name}
                                        sub={`${prettyBytes(scan.size)} · ${new Date(scan.lastModified)
                                            .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                                            + (filed ? ' · already filed' : '')}
                                        /* ⛔ NAME ONLY - no objectUrl, no target, so this is the
                                           file-type glyph and not an image. See the note above. */
                                        icon={<DocumentThumb fileName={scan.name} size={28} />}
                                        title={filed
                                            ? 'Already filed — drag it again to put it on another appointment'
                                            : 'Drag onto an appointment or a driver'}
                                        onPreview={() => void previewLocal(scan)}
                                        /* ⭐ PARITY WITH THE SHAREPOINT ROW — Pete:
                                           *"Where are my other icons next to the eye?
                                           Like Delete?"* */
                                        onPrintPaper={() => void printLocal(scan)}
                                        onDelete={() => setConfirmDelete([scan])}
                                        deleteTitle="Delete this file from the folder on this machine — permanent, there is no recycle bin"
                                        selected={ticked.has(scan.ref)}
                                        onToggleSelected={() => setTicked(prev => {
                                            const next = new Set(prev);
                                            if (next.has(scan.ref)) next.delete(scan.ref); else next.add(scan.ref);
                                            return next;
                                        })}
                                        /* ⛔ COMPUTED AT DRAGSTART, not at render — the
                                           selection can change in between. */
                                        dragItems={() => (ticked.has(scan.ref)
                                            ? scans.filter(x => ticked.has(x.ref))
                                                .map(x => ({ kind: 'localScan' as const, ref: x.ref, name: x.name }))
                                            : [{ kind: 'localScan' as const, ref: scan.ref, name: scan.name }])}
                                    />
                                );
                            })}
                        </div>
                        </>
                    )}

                    {/* ⛔ ~~"Nothing in the scan folder yet."~~ REMOVED (Pete, 2026-09-10:
                        *"need to remove 'Nothing in the scan folder yet' - not
                        necessary"*). ⚠️ An empty panel under a header that names the
                        view already says this; the sentence was one more line of
                        chrome in a rail that is short of room. */}

                    {/* ---- the unfiled tray -------------------------------- */}
                    {/**
                      * ⛔⛔ ~~THE UNFILED-DOCUMENT LIST~~ REMOVED 2026-09-10 (§0.3).
                      *
                      * ⚠️ IT HAD BEEN UNREACHABLE SINCE THIS PANEL STOPPED FETCHING
                      * DOCUMENTS. `refreshUnfiled` sets `[]` and nothing else writes
                      * `unfiled`, because the local view is the folder on this PC and
                      * documents in Dock belong to the cloud pile — see the long note
                      * on that split above.
                      *
                      * ⛔ AND IT WAS ~200 LINES OF A HAND-WRITTEN DOCUMENT ROW, a third
                      * copy of what `TrayCard` draws. Dead code that duplicates a
                      * shared component is not harmless: it is where the next person
                      * "fixes" something and the drift starts again, which is the exact
                      * history this whole change is repairing.
                      */}
                </div>
            )}

            {/* ⛔ NEVER `window.confirm` (§4.0), and never silent: `removeEntry`
                has no undo anywhere in the browser or the OS shell. */}
            {confirmDelete && (
                <ConfirmDialog
                    open={true}
                    destructive
                    title={confirmDelete.length === 1
                        ? `Delete ${confirmDelete[0].name}?`
                        : `Delete ${confirmDelete.length} files?`}
                    message={
                        `This permanently deletes ${confirmDelete.length === 1 ? 'this file' : 'these files'} from `
                        + `${handle?.name ?? 'the scan folder'} on this machine. There is no recycle bin behind it — `
                        + 'unlike the SharePoint tray, where a deleted scan is recoverable for 93 days.'
                    }
                    confirmLabel={confirmDelete.length === 1 ? 'Delete file' : `Delete ${confirmDelete.length} files`}
                    onConfirm={() => void doDeleteLocal(confirmDelete)}
                    onCancel={() => setConfirmDelete(null)}
                />
            )}
            <SignDocumentDialog
                open={signing !== null}
                document={signing}
                facilityCode={facilityCode}
                carrierCode={
                    // ⭐ Scopes "where did this carrier's stamp go last time" — the
                    // layout is a property of the carrier's paperwork, not ours.
                    //
                    // ⚠️ COMPARED AS STRINGS. AppointmentDto.appointmentId is a
                    // NUMBER and DocumentDto.appointmentId is a STRING (a BIGINT
                    // that would lose precision as a JS number), so `===` between
                    // them is always false — the compiler caught it, and without
                    // it the carrier lookup would have silently never matched and
                    // the remembered placement never been offered.
                    signedAppointment?.carrierCode ?? null
                }
                appointmentNumber={signedAppointment?.appointmentNumber ?? null}
                getToken={getToken}
                onClose={() => setSigning(null)}
                onSigned={() => { onFiled?.(); }}
            />
        </div>
    );
}
